import moment from 'moment';

import Exception from '../models/Exception';
import ExceptionRepository from '../repositories/ExceptionRepository';

class ExceptionService {
  static TYPES = {
    SHIFT_REPLACEMENT: 'shift_replacement',
  };

  constructor() {
    this.repository = new ExceptionRepository();
  }

  /**
   * Validate exception data
   * @param {Object} exceptionData - Exception data to validate
   * @returns {Object} Object with isValid (boolean) and errors (array)
   */
  static validateException(exceptionData) {
    const errors = [];

    if (!exceptionData.type || exceptionData.type.trim() === '') {
      errors.push('Exception type is required');
    }

    if (!exceptionData.startDate || !exceptionData.endDate) {
      errors.push('Start date and end date are required');
    }

    if (exceptionData.startDate && exceptionData.endDate) {
      if (moment(exceptionData.startDate).isAfter(moment(exceptionData.endDate))) {
        errors.push('Start date must be before or equal to end date');
      }
    }

    if (!exceptionData.user) {
      errors.push('User is required');
    }

    if (exceptionData.type === ExceptionService.TYPES.SHIFT_REPLACEMENT) {
      if (!exceptionData.replacedUser?.id) {
        errors.push('replacedUser is required for shift_replacement exceptions');
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Clean and normalize exception data
   * @param {Object} exceptionData - Raw exception data
   * @returns {Object} Cleaned exception data
   */
  cleanExceptionData(exceptionData) {
    const cleanedData = { ...exceptionData };

    // Ensure dates are in YYYY-MM-DD format
    if (cleanedData.startDate) {
      cleanedData.startDate = moment(cleanedData.startDate).format('YYYY-MM-DD');
    }
    if (cleanedData.endDate) {
      cleanedData.endDate = moment(cleanedData.endDate).format('YYYY-MM-DD');
    }

    // Ensure isExcused is boolean
    if (typeof cleanedData.isExcused === 'string') {
      cleanedData.isExcused = cleanedData.isExcused === 'true';
    }
    if (cleanedData.isExcused === undefined || cleanedData.isExcused === null) {
      cleanedData.isExcused = false;
    }

    // Ensure user is an object
    if (cleanedData.user && typeof cleanedData.user !== 'object') {
      console.warn('User should be an object');
    }

    return cleanedData;
  }

  /**
   * Create a new exception
   * @param {Object} exceptionData - Exception data
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object>} Created exception
   */
  async createException(exceptionData, organizationId) {
    const validation = ExceptionService.validateException(exceptionData);
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    const cleanedData = this.cleanExceptionData(exceptionData);

    const exception = new Exception({
      ...cleanedData,
    });

    return this.repository.create(exception.toCreate(), organizationId);
  }

  /**
   * Create a shift_replacement exception. Reusable from cover flows and elsewhere.
   * On create, orgExceptionOnCreate applies AttendanceReplaceService to attendance records.
   *
   * @param {Object} params
   * @param {Object} params.user - Substitute user (TUserSummary)
   * @param {Object} params.replacedUser - Absent user being covered (TUserSummary)
   * @param {string} params.startDate - YYYY-MM-DD
   * @param {string} params.endDate - YYYY-MM-DD
   * @param {string} [params.replacementWorkShiftId]
   * @param {string} [params.replacementWorkJourneyConfigId]
   * @param {string} [params.description]
   * @param {boolean} [params.isExcused=true]
   * @param {string} organizationId
   * @returns {Promise<Object>} Created exception
   */
  async createShiftReplacementException(
    {
      user,
      replacedUser,
      startDate,
      endDate,
      replacementWorkShiftId,
      replacementWorkJourneyConfigId,
      description,
      isExcused = true,
    },
    organizationId,
  ) {
    return this.createException(
      {
        type: ExceptionService.TYPES.SHIFT_REPLACEMENT,
        user,
        replacedUser,
        startDate,
        endDate,
        replacementWorkShiftId,
        replacementWorkJourneyConfigId,
        description,
        isExcused,
      },
      organizationId,
    );
  }

  /**
   * Update an existing exception
   * @param {string} exceptionId - Exception ID
   * @param {Object} exceptionData - Updated exception data
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object>} Updated exception
   */
  async updateException(exceptionId, exceptionData, organizationId) {
    const validation = ExceptionService.validateException(exceptionData);
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    const cleanedData = this.cleanExceptionData(exceptionData);

    const exception = new Exception({
      ...cleanedData,
    });

    return this.repository.update(exceptionId, exception.toUpdate(), organizationId);
  }

  /**
   * Delete an exception
   * @param {string} exceptionId - Exception ID
   * @param {string} organizationId - Organization ID
   * @returns {Promise<void>}
   */
  async deleteException(exceptionId, organizationId) {
    return this.repository.delete(exceptionId, organizationId);
  }

  /**
   * Get an exception by ID
   * @param {string} exceptionId - Exception ID
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object>} Exception data
   */
  async getExceptionById(exceptionId, organizationId) {
    const exceptionData = await this.repository.getExceptionById(exceptionId, organizationId);
    return Exception.fromData(exceptionData);
  }

  /**
   * Get all exceptions for an organization with optional filters
   * @param {string} organizationId - Organization ID
   * @param {Object} filters - Optional filters (type, isExcused, userId)
   * @returns {Promise<Array>} Array of exceptions
   */
  async getAllExceptions(organizationId, filters = {}) {
    const exceptions = await this.repository.getAllExceptions(organizationId, filters);
    return exceptions.map(exception => Exception.fromData(exception));
  }

  /**
   * Get exceptions by user ID
   * @param {string} userId - User ID
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Array>} Array of user exceptions
   */
  async getExceptionsByUserId(userId, organizationId) {
    const exceptions = await this.repository.getExceptionsByUserId(userId, organizationId);
    return exceptions.map(exception => Exception.fromData(exception));
  }

  /**
   * Get exception statistics for an organization
   * @param {string} organizationId - Organization ID
   * @param {Object} dateRange - Optional date range filter
   * @returns {Promise<Object>} Exception statistics
   */
  async getExceptionStats(organizationId, dateRange = null) {
    const allExceptions = await this.getAllExceptions(organizationId);
    
    let filteredExceptions = allExceptions;
    
    // Filter by date range if provided
    if (dateRange && dateRange.startDate && dateRange.endDate) {
      const startDate = moment(dateRange.startDate);
      const endDate = moment(dateRange.endDate);
      
      filteredExceptions = allExceptions.filter(exception => {
        const exceptionStart = moment(exception.startDate);
        const exceptionEnd = moment(exception.endDate);
        
        return exceptionStart.isBetween(startDate, endDate, null, '[]') ||
               exceptionEnd.isBetween(startDate, endDate, null, '[]') ||
               (exceptionStart.isBefore(startDate) && exceptionEnd.isAfter(endDate));
      });
    }

    const stats = {
      total: filteredExceptions.length,
      excused: filteredExceptions.filter(e => e.isExcused).length,
      unexcused: filteredExceptions.filter(e => !e.isExcused).length,
      byType: {},
    };

    // Group by type
    filteredExceptions.forEach(exception => {
      if (!stats.byType[exception.type]) {
        stats.byType[exception.type] = 0;
      }
      stats.byType[exception.type]++;
    });

    return stats;
  }
}

export default ExceptionService;
